<template>
  <div
    class="home-page min-h-screen overflow-x-hidden"
    :class="isDark ? 'home-page--dark' : 'home-page--light'"
  >
    <div class="home-bg" aria-hidden="true">
      <div class="home-bg__orb home-bg__orb--1" />
      <div class="home-bg__orb home-bg__orb--2" />
      <div class="home-bg__orb home-bg__orb--3" />
      <div class="home-bg__grid" />
    </div>

    <header
      class="home-nav fixed top-0 inset-x-0 z-50 px-4 sm:px-6 py-4 home-nav--visible"
    >
      <div class="home-nav__inner max-w-7xl mx-auto flex items-center justify-between rounded-2xl border backdrop-blur-xl px-5 py-3">
        <router-link to="/" class="flex items-center gap-3 group">
          <img
            src="https://tiye.ams3.cdn.digitaloceanspaces.com/tiye_assets/TIYE-main-logo.png"
            alt="Tiye"
            class="h-7 w-auto transition-opacity group-hover:opacity-80"
            :class="isDark ? 'brightness-0 invert opacity-90' : 'opacity-90'"
          />
          <span class="hidden sm:inline home-nav__divider h-4 w-px" />
          <span class="hidden sm:inline text-xs font-bold tracking-[0.25em] home-nav__brand uppercase">Move</span>
        </router-link>
        <div class="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            class="home-theme-toggle"
            :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
            @click="toggleTheme"
          >
            <span class="material-icons">{{ isDark ? 'light_mode' : 'dark_mode' }}</span>
          </button>
          <router-link to="/" class="home-btn-ghost hidden sm:inline-flex">
            Back to home
          </router-link>
        </div>
      </div>
    </header>

    <main class="relative z-10 flex min-h-screen items-center justify-center px-4 sm:px-6 pt-24 pb-12">
      <div class="login-card home-reveal w-full max-w-md" style="--delay: 0.15s">
        <div class="text-center mb-8">
          <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-tiye-green/30 bg-tiye-green/10 text-tiye-green text-xs font-semibold mb-5">
            <span class="home-pulse-dot" />
            Tiye Move
          </div>
          <h1 class="text-2xl sm:text-3xl font-extrabold home-text mb-2 tracking-tight">
            Welcome <span class="home-gradient-text">back</span>
          </h1>
          <p class="home-text-muted text-sm sm:text-base">
            Use the email on your Tiye account and the password you already have. If you were invited, nothing new was created — sign in with that same password.
          </p>
        </div>

        <div v-if="loading" class="flex flex-col items-center py-8 gap-3">
          <div class="login-spinner" aria-hidden="true" />
          <p class="home-text-muted text-sm">Signing in...</p>
        </div>

        <div v-if="error && !loading" class="login-error mb-5" role="alert">
          {{ error }}
        </div>

        <form v-if="!loading" class="space-y-5" @submit.prevent="handleSubmit">
          <div class="login-field">
            <label for="email">Email</label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              autocomplete="email"
              class="login-input"
              placeholder="you@email.com"
            />
          </div>

          <div class="login-field">
            <label for="password">Password</label>
            <div class="login-input-wrap">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                required
                autocomplete="current-password"
                class="login-input"
                placeholder="Enter your password"
              />
              <button
                type="button"
                class="login-input-toggle"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                @click="showPassword = !showPassword"
              >
                <span class="material-icons">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
              </button>
            </div>
          </div>

          <div class="flex justify-end">
            <router-link
              to="/reset-password"
              class="text-sm font-semibold text-tiye-green hover:opacity-80 transition-opacity"
            >
              Forgot password?
            </router-link>
          </div>

          <button
            type="submit"
            class="home-btn-primary w-full"
            :disabled="loading"
          >
            <span>Sign in</span>
            <span class="material-icons text-lg">login</span>
          </button>
        </form>

        <p class="mt-8 text-center text-sm home-text-faint leading-relaxed">
          Need access? Contact your Move administrator.
        </p>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useMoveTheme } from '../composables/useMoveTheme'

export default {
  name: 'LoginView',
  setup () {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const { isDark, toggleTheme } = useMoveTheme()
    const email = ref('')
    const password = ref('')
    const showPassword = ref(false)

    const loading = computed(() => store.state.auth.loading)
    const error = computed(() => store.state.auth.error)

    const handleSubmit = async () => {
      const result = await store.dispatch('auth/login', {
        email: email.value,
        password: password.value
      })

      if (result.success) {
        if (route.query.redirect) {
          router.push(route.query.redirect)
        } else if (store.getters['auth/hasProductionAccess'] && !store.getters['auth/isMoveAdmin']) {
          router.push('/production')
        } else {
          router.push('/dashboard')
        }
      }
    }

    return {
      email,
      password,
      showPassword,
      loading,
      error,
      handleSubmit,
      isDark,
      toggleTheme
    }
  }
}
</script>
